import React from "react";
import ReactECharts from "echarts-for-react";
import { groupSeriesXY } from "../utils/echartsSeries.js";
import { getModaliteColor, LABELS_POL, ANNEES_ANCRAGE } from "../constants.js";

// Au chargement de la page, seules ces courbes sont visibles (libellés
// LABELS_POL) : les autres se cochent depuis la légende.
const COURBES_VISIBLES_PAR_DEFAUT = ["Irrigué 100%", "Sec", "Faible", "Stricte"];

export default function MargeChart({ data, typeMarge }) {
  // Sélection de la légende conservée dans un état : sans cela, chaque rendu
  // (notMerge) remettrait la sélection par défaut et annulerait les cases
  // cochées par l'utilisateur.
  const [selectionLegende, setSelectionLegende] = React.useState(null);

  const valueKey = typeMarge === "cumul" ? "marge_cumulee" : "marge_annuelle";
  const groups = groupSeriesXY(data, { xKey: "year", groupKey: "modalite", valueKey });

  const anneeRef = ANNEES_ANCRAGE[0];
  const valeurRef = (points) => {
    const point = points.find((p) => p.x === anneeRef);
    return point ? point.y : -Infinity;
  };
  const groupsTries = [...groups].sort((a, b) => valeurRef(b.points) - valeurRef(a.points));

  const series = groups.map(({ group, points }) => ({
    name: LABELS_POL[group] || group,
    type: "line",
    symbol: "none",
    lineStyle: { width: 2, color: getModaliteColor(group) },
    itemStyle: { color: getModaliteColor(group) },
    data: points.map((p) => [p.x, Math.round(p.y)]),
  }));

  // Minimum de toutes les valeurs
 const minValue = Math.min(
    ...groups.flatMap(({ points }) => points.map((p) => p.y))
  ) * 0.95;

  const yMin = Math.floor(minValue / 100) * 100;

  const maxValue = Math.max(
    ...groups.flatMap(({ points }) => points.map((p) => p.y))
  ) * 1;

  const yMax = Math.ceil(maxValue / 100) * 100;

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const sorted = [...params].sort((a, b) => {
          const va = Array.isArray(a.value) ? a.value[1] : a.value;
          const vb = Array.isArray(b.value) ? b.value[1] : b.value;
          return vb - va;
        });
        let html = `${String(sorted[0]?.axisValue ?? "")}<br/>`;
        sorted.forEach((p) => {
          const v = Array.isArray(p.value) ? p.value[1] : p.value;
          html += `${p.marker} ${p.seriesName}: ${v}<br/>`;
        });
        return html;
      },
    },
    legend: {
      bottom: 0,
      type: "plain",
      textStyle: { fontSize: 14 },
      data: groupsTries.map((g) => LABELS_POL[g.group] || g.group),
      selected: selectionLegende ?? Object.fromEntries(
        groupsTries.map((g) => {
          const nom = LABELS_POL[g.group] || g.group;
          return [nom, COURBES_VISIBLES_PAR_DEFAUT.includes(nom)];
        })
      ),
    },
    grid: { left: 60, right: 20, top: 50, height: 380, containLabel: true },
    xAxis: {
      type: "value",
      name: "Année",
      nameLocation: "middle",
      nameGap: 30,
      // L'axe démarre à la première année réellement présente dans les
      // données, et non à une valeur figée (2010 auparavant), sinon le
      // graphique laisse une plage vide à gauche.
      min: Math.min(...data.map((d) => Number(d.year))),
      max: 2050,
      axisLabel: { formatter: (v) => String(v) },
    },
    yAxis: {
      type: "value",
      min: yMin,
      max: yMax,
      name: typeMarge === "cumul" ? "Marge cumulée" : "Marge annuelle",
      nameLocation: "end",
      nameGap: 15,
      nameTextStyle: { fontSize: 13, fontWeight: 600, color: "#000", align: "left" },
    },
    series:
      typeMarge === "annuelle"
        ? [
            ...series,
            {
              name: "zéro", type: "line", data: [], markLine: {
                silent: true, symbol: "none", lineStyle: { color: "#999", type: "dashed" },
                data: [{ yAxis: 0 }],
              }, tooltip: { show: false }, legendHoverLink: false,
            },
          ]
        : series,
  };

  const ANNEES_TABLEAU = [2025, 2035, 2050];

  const lignesTableau = groupsTries.map(({ group, points }) => {
    const valeurs = ANNEES_TABLEAU.map((a) => {
      const point = points.find((p) => p.x === a);
      return point ? Math.round(point.y) : null;
    });
    return { group, label: LABELS_POL[group] || group, valeurs };
  });

  return (
    <div className="oad-pheno-layout" style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
      <div style={{ flex: "1.5 1 0", display: "flex" }}>
        <ReactECharts
          option={option}
          style={{ height: "100%", minHeight: 500, width: "100%" }}
          notMerge
          lazyUpdate
          onEvents={{ legendselectchanged: (e) => setSelectionLegende(e.selected) }}
        />
      </div>

      <div style={{ flex: "1 1 0", overflowX: "auto" }}>
        <table className="oad-data-table oad-table-centered">
          <thead>
            <tr>
              <th></th>
              {ANNEES_TABLEAU.map((a) => (
                <th key={a}>{a}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lignesTableau.map((ligne) => (
              <tr key={ligne.group}>
                <td>{ligne.label}</td>
                {ligne.valeurs.map((v, i) => (
                  <td key={i}>{v == null ? "—" : v.toLocaleString("fr-FR")}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}